// Radar custom-axis editor (04h-4c-2) — a popover that lets a user add any of the catalog's 162
// metrics onto a radar, remove one of its default axes, or drag-reorder them. Ported from
// renderers-radar.js's openRadarAxisEditor as a **React portal** to document.body (Fork A):
// the popover deliberately lives OUTSIDE the module's own re-rendered DOM, so a reshape (which
// re-renders the chart + table) never disturbs it mid-edit — the same append-to-body placement
// the legacy uses for the exact same reason.
//
// The one source of truth is mod._radarCustomAxes (an ordered metric-key array); every edit
// writes it through and calls onChange() to reshape the module. A local `keys` state mirrors it
// for the popover's own rendering + the drag (the legacy kept the same local `keys` array).

import { fuzzyMatches } from "../data/fuzzy-search.js";
import { LIVE } from "../live/flag.js";
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { getModState, setModState } from "./bridge.js";
import { radarAxisKeys } from "../hooks/useRadarAxes.js";
import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";

// A radar must keep at least 3 axes (fewer can't form a shape). renderers-radar.js:1168.
const MIN_RADAR_AXES = 3;
const POPOVER_WIDTH = 280;

// The two-slider "adjust which metrics are shown" icon (renderers-radar.js:1148), as JSX.
function EditAxesIcon() {
  return (
    <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3">
      <line x1="2" y1="4.5" x2="14" y2="4.5" />
      <circle cx="6" cy="4.5" r="1.6" fill="currentColor" stroke="none" />
      <line x1="2" y1="11.5" x2="14" y2="11.5" />
      <circle cx="10" cy="11.5" r="1.6" fill="currentColor" stroke="none" />
    </svg>
  );
}

interface Props {
  anchor: HTMLElement;
  mod: HTMLElement;
  template: string;
  categories: readonly ProtoMetricCategory[];
  catalog: Record<string, ProtoPlayerMetric>;
  /** Reshape the module (re-render the chart + table from the new mod._radarCustomAxes). */
  onChange: () => void;
  onClose: () => void;
}

/** One group of the "Add a metric" list. */
export interface AxisEditorGroup {
  id: string;
  name: string;
  metrics: readonly { key: string; name: string; shortName?: string | null }[];
}

/** The player Radar's editor: its picks are mod._radarCustomAxes, its list the player stats. */
export function RadarAxisEditor({ anchor, mod, template, categories, catalog, onChange, onClose }: Props) {
  const groups = useMemo<AxisEditorGroup[]>(
    () => categories.map((cat) => ({ id: cat.id, name: cat.name, metrics: cat.subcategories.flatMap((s) => s.playerMetrics) })),
    [categories],
  );
  return (
    <AxisEditor
      anchor={anchor}
      // Editing starts from whatever's currently on screen — the module's existing customization if
      // it has one, else the template default (renderers-radar.js:1182) — the same list the chart shows.
      initialKeys={radarAxisKeys(template, getModState<string[]>(mod, "_radarCustomAxes"))}
      groups={groups}
      labelOf={(key) => {
        const cat = catalog[key];
        return cat ? cat.shortName || cat.name : key;
      }}
      onPersist={(next) => {
        setModState(mod, "_radarCustomAxes", next);
        onChange();
      }}
      onClose={onClose}
    />
  );
}

interface AxisEditorProps {
  anchor: HTMLElement;
  /** The spokes on screen when it opens. */
  initialKeys: readonly string[];
  groups: readonly AxisEditorGroup[];
  labelOf: (key: string) => string;
  /** Keep the new list and reshape the module. */
  onPersist: (keys: string[]) => void;
  /** Back to the template's own spokes (the team Radar); no button without it. */
  onReset?: () => readonly string[];
  onClose: () => void;
  /** The fewest it may keep (a Radar: 3, a shape; the Stats Table: 1 column). */
  minKeys?: number;
  /** The picked list's heading ("On this radar"; the Stats Table: "In this table"). */
  heading?: string;
}

/** The popover itself, for any Radar (a player's above, a team's in TeamRadar) and the Stats
 *  Table's columns. */
export function AxisEditor({
  anchor,
  initialKeys,
  groups,
  labelOf,
  onPersist,
  onReset,
  onClose,
  minKeys = MIN_RADAR_AXES,
  heading = "On this radar",
}: AxisEditorProps) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const rowsRef = useRef<HTMLDivElement>(null);

  const [keys, setKeys] = useState<string[]>(() => initialKeys.slice());
  // Latest keys for the drag handlers (their closures would otherwise capture a stale array).
  const keysRef = useRef(keys);
  keysRef.current = keys;
  const [draggingIdx, setDraggingIdx] = useState<number | null>(null);
  const [query, setQuery] = useState("");

  // Position under the anchor, clamped to the viewport (renderers-radar.js:1193). Computed once.
  const pos = useMemo(() => {
    const rect = anchor.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    return {
      left: Math.max(8, Math.min(rect.right + window.scrollX - POPOVER_WIDTH, vw - POPOVER_WIDTH - 8)),
      top: rect.bottom + window.scrollY + 4,
    };
  }, [anchor]);

  // Persist to the element + reshape the module. Kept as one helper so add/remove/drop all agree.
  const persist = (next: string[]) => onPersist(next.slice());

  // Outside-click closes — but never on the anchor button (whose own onClick toggles us), exactly
  // like the legacy's `!e.target.closest('.radar-edit-axes-btn')` guard (renderers-radar.js:1321).
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      // An add/remove click re-renders the list and detaches the very row that was clicked before
      // this bubbling handler runs — a detached target is our OWN action, not an outside click, so
      // the `contains` check below would wrongly close us. Ignore it (keeps the editor open to add
      // or remove several, matching the legacy's rebuild-in-place). isConnected is false once React
      // has removed the node from the document.
      if (!t.isConnected) return;
      if (popoverRef.current?.contains(t)) return;
      if (t.closest(".radar-edit-axes-btn")) return;
      onClose();
    };
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, [onClose]);

  const removeAxis = (idx: number) => {
    if (keysRef.current.length <= minKeys) return;
    const next = keysRef.current.slice();
    next.splice(idx, 1);
    setKeys(next);
    persist(next);
  };
  const addAxis = (key: string) => {
    const next = [...keysRef.current, key];
    setKeys(next);
    persist(next);
  };

  // Drag-reorder: a pointer drag over the rows moves the dragged key to whichever row it's over.
  // Like the legacy, a reorder reshapes the module only on DROP (the add-list order doesn't depend
  // on axis order, so no live rebuild is needed while dragging). renderers-radar.js:1245.
  const startDrag = (i: number, e: ReactPointerEvent<HTMLElement>) => {
    e.preventDefault();
    let fromIdx = i;
    setDraggingIdx(i);
    const onMove = (ev: PointerEvent) => {
      const rows = rowsRef.current;
      if (!rows) return;
      const overRow = Array.from(rows.children).find((r) => {
        const rc = r.getBoundingClientRect();
        return ev.clientY >= rc.top && ev.clientY <= rc.bottom;
      }) as HTMLElement | undefined;
      if (!overRow) return;
      const toIdx = Number(overRow.dataset.idx);
      if (Number.isNaN(toIdx) || toIdx === fromIdx) return;
      const next = keysRef.current.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved!);
      keysRef.current = next; // so the next move measures against the reordered set
      fromIdx = toIdx;
      setKeys(next);
      setDraggingIdx(toIdx);
    };
    const onUp = () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      setDraggingIdx(null);
      persist(keysRef.current);
    };
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  };

  // Add-list: grouped by category (metrics flattened across subcategories), already-selected keys
  // excluded, filtered by the search query. renderers-radar.js:1216.
  const addGroups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const selected = new Set(keys);
    return groups
      .map((cat) => ({
        cat,
        rows: cat.metrics
          .filter((m) => {
            if (selected.has(m.key)) return false;
            // Live: forgiving (accents, word order, one typo — fuzzy-search.ts); the demo keeps its plain match.
            if (q && LIVE && !fuzzyMatches(q, `${m.name} ${m.shortName ?? ""}`)) return false;
            if (q && !LIVE && !m.name.toLowerCase().includes(q) && !(m.shortName ?? "").toLowerCase().includes(q)) return false;
            return true;
          }),
      }))
      .filter((g) => g.rows.length);
  }, [groups, keys, query]);

  return createPortal(
    <div className="radar-axis-editor" ref={popoverRef} style={{ left: pos.left, top: pos.top }}>
      <div className="radar-axis-editor-section">
        <div className="radar-axis-editor-heading">
          {heading}
          {onReset && (
            <button
              type="button"
              className="radar-axis-reset-btn"
              onClick={() => {
                const next = onReset().slice();
                setKeys(next);
              }}
            >
              Reset
            </button>
          )}
        </div>
        <div className="radar-axis-rows" ref={rowsRef}>
          {keys.map((key, i) => {
            const label = labelOf(key);
            return (
              <div className={`radar-axis-row${draggingIdx === i ? " dragging" : ""}`} data-idx={i} key={key}>
                <span className="radar-axis-drag-handle" title="Drag to reorder" onPointerDown={(e) => startDrag(i, e)}>
                  ⠿
                </span>
                <span className="radar-axis-row-label">{label}</span>
                <button
                  type="button"
                  className="radar-axis-remove-btn"
                  disabled={keys.length <= minKeys}
                  title="Remove"
                  onClick={() => removeAxis(i)}
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      </div>
      <div className="radar-axis-editor-section">
        <div className="radar-axis-editor-heading">Add a metric</div>
        <input
          className="radar-axis-editor-search"
          placeholder="Search metrics…"
          autoComplete="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="radar-axis-add-list">
          {addGroups.length ? (
            addGroups.map((g) => (
              <div className="radar-axis-add-group" key={g.cat.id}>
                <div className="radar-axis-add-group-label">{g.cat.name}</div>
                {g.rows.map((m) => (
                  <div className="radar-axis-add-row" key={m.key} onClick={() => addAxis(m.key)}>
                    <span className="radar-axis-add-plus">+</span>
                    {m.name}
                  </div>
                ))}
              </div>
            ))
          ) : (
            <div className="value-picker-empty">No matches</div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// The sub-controls button that opens the editor. Rendered inside main.js's strip (via
// SubControlsSlot); its ref anchors the popover. Toggles the editor open/closed.
export function RadarEditAxesButton({ open, onToggle, btnRef }: {
  open: boolean;
  onToggle: () => void;
  btnRef: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={btnRef}
      type="button"
      className="radar-edit-axes-btn"
      aria-expanded={open}
      title="Add or remove metrics"
      onClick={onToggle}
    >
      <EditAxesIcon />
    </button>
  );
}
